/**
 * What "Check again" settled, as main PROVED it (review fixes C #1): `settled` in the `hns-pending-moves recheck`
 * answer — the move finished (with the resource to select) or was never applied. A record that is gone without that
 * proof (dismissed elsewhere, say) is `unknown`: the renderer never infers success from a record's absence.
 */
import type { HnsLocator, HnsPathRef, HnsPendingMove, HnsPendingMoveSettlement } from '../../../../types/azureStorage';
import type { HnsActionOutcome } from './hnsActionAnalytics';

export type SettledMove = { state: 'moved'; resource: HnsPathRef } | { state: 'not-moved' } | { state: 'unknown' } | { state: 'pending'; move: HnsPendingMove };

export function settledMoveOf(answer: { moves: HnsPendingMove[]; settled?: HnsPendingMoveSettlement }, id: string): SettledMove {
  const still = answer.moves.find((move) => move.id === id);
  if (still) return { state: 'pending', move: still };
  const settled = answer.settled?.id === id ? answer.settled : undefined;
  if (settled?.outcome === 'moved') return { state: 'moved', resource: settled.resource };
  return settled?.outcome === 'not-applied' ? { state: 'not-moved' } : { state: 'unknown' };
}

/** `feature:storage_hns_action` outcome of a Check again: `ok` only for a PROVED completion (a move that was not applied is the move's failure). */
export const settledMoveOutcome = (settled: SettledMove): HnsActionOutcome =>
  settled.state === 'moved' ? 'ok' : settled.state === 'not-moved' ? 'error' : 'uncertain';

export const settledMoveText = (settled: SettledMove, source: HnsLocator): string => {
  const label = source.path ? `${source.fileSystem}/${source.path}` : source.fileSystem;
  if (settled.state === 'moved') return `The move of ${label} finished.`;
  if (settled.state === 'not-moved') return `Nothing was moved: ${label} was not changed.`;
  return 'This move is no longer pending; the list was refreshed — check both locations.';
};
